<template>
  <div class="date__header">
    <p>{{ `Bitte ${isMobile ? 'Tag' : 'Woche'} wählen` }}</p>
    <IconClose @click="closeCalendar" aria-hidden="true" />
  </div>
  <hr />
  <div class="date__calendar">
    <el-config-provider :locale="locale">
      <el-date-picker
        v-if="isMobile"
        v-model="date"
        type="date"
        value-format="YYYY-MM-DD"
        :disabled-date="isDateDisabled"
        :default-date="singleDate"
        @panel-change="onPanelChange"
        ref="datePickerEl"
      >
        <template #default="cell">
          <p
            class="cell"
            :class="{
              available: isDateAvailable(cell),
              selected: isDateSelected(cell),
            }"
          >
            {{ cell.text }}
          </p>
        </template>
      </el-date-picker>
      <el-date-picker
        v-else
        v-model="week"
        type="week"
        value-format="YYYY-MM-DD"
        :disabled-date="isDateDisabled"
        @panel-change="onPanelChange"
        ref="weekPickerEl"
      >
        <template #default="cell">
          <p class="cell" :class="{ available: isDateAvailable(cell) }">
            {{ cell.text }}
          </p>
        </template>
      </el-date-picker>
    </el-config-provider>
  </div>
</template>

<script>
import { convertDateFormat, getFirstDateOfMonth, getLastSunday, getPreviousDate } from '@/utils/format.js'
import { ElConfigProvider } from 'element-plus'
import de from 'element-plus/lib/locale/lang/de'
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'

import IconClose from '@/components/icons/IconClose.vue'

export default {
  components: { ElConfigProvider, IconClose },
  emits: ['date', 'week', 'closeCalendar', 'panelChange'],
  props: {
    showCalendar: {
      type: Boolean,
    },
  },
  data() {
    return {
      locale: de,
      date: '',
      week: getLastSunday(),
      populatedMonths: [],
      debouncedPopulateNewMonth: null,
    }
  },
  created() {
    // Create debounced version of populateNewMonth with 400ms delay
    // Using 'trailing: true' ensures the function is called after debounce period ends
    // Using 'leading: false' prevents immediate execution on first call
    this.debouncedPopulateNewMonth = debounce(this.populateNewMonth, 400, {
      leading: false,
      trailing: true,
    })
  },
  mounted() {
    const wrapper = document.querySelector('.date__calendar')
    const popper = document.querySelector('.el-picker__popper')
    wrapper.appendChild(popper)

    // on first appointment page load in mobile only mounted is called (not activated)
    if (this.isMobile) {
      if (this.sessionData) this.populateNewMonth(new Date(this.singleDate))
    }

    // useless since the component is mounted on app start and not when the popup is opened
    // if(this.$refs.weekPickerEl) this.$refs.weekPickerEl.focus()
    // if(this.$refs.datePickerEl) this.$refs.datePickerEl.focus()
  },
  activated() {
    if (this.isPreviousDataPopulated) {
      this.populateNewMonth(new Date(this.singleDate))
    }
  },
  deactivated() {
    // reset everything
    this.date = this.singleDate
    this.populatedMonths = []
    this.$store.commit('clearDatesWithTimeslots')

    // Cancel any pending debounced calls
    if (this.debouncedPopulateNewMonth && this.debouncedPopulateNewMonth.cancel) {
      this.debouncedPopulateNewMonth.cancel()
    }
  },

  beforeUnmount() {
    // Clean up the debounced function
    if (this.debouncedPopulateNewMonth && this.debouncedPopulateNewMonth.cancel) {
      this.debouncedPopulateNewMonth.cancel()
    }
  },
  computed: {
    ...mapGetters([
      'isMobile',
      'sessionData',
      'kfzartId',
      'activeStationId',
      'singleDate',
      'firstDateOfWeek',
      'datesWithTimeslots',
    ]),
    isPreviousDataPopulated() {
      // data populated on entering url
      return this.sessionData && this.kfzartId && this.activeStationId
    },
  },
  watch: {
    isPreviousDataPopulated() {
      if (this.isPreviousDataPopulated) this.populateNewMonth(new Date(this.singleDate))
    },
    date() {
      this.$emit('date', this.date)
      this.closeCalendar()
    },
    week() {
      this.$emit('week', this.week)
      this.closeCalendar()
    },
    singleDate() {
      this.date = this.singleDate
    },
    firstDateOfWeek() {
      this.week = this.firstDateOfWeek
    },
    showCalendar() {
      if (this.showCalendar) {
        this.populateNewMonth(new Date(this.isMobile ? this.singleDate : this.firstDateOfWeek))
      }
    },
  },
  methods: {
    isDateDisabled(time) {
      return convertDateFormat(time) < getPreviousDate(new Date(), 1)
    },
    isDateAvailable({ dayjs }) {
      return this.datesWithTimeslots.includes(dayjs.format('YYYY-MM-DD'))
    },
    isDateSelected({ dayjs }) {
      const selectedDate = this.date || new Date()
      return convertDateFormat(new Date(selectedDate)) === dayjs.format('YYYY-MM-DD')
    },
    onPanelChange(date) {
      this.debouncedPopulateNewMonth(date)
    },
    populateNewMonth(date) {
      const monthNumber = date.getMonth() + 1 // 1-12
      const year = date.getFullYear()
      const monthYearKey = `${year}-${monthNumber.toString().padStart(2, '0')}`
      
      if (this.populatedMonths.includes(monthYearKey)) {
        console.warn('Month already populated:', monthYearKey)
        return
      }
      this.populatedMonths.push(monthYearKey)

      const currentDate = new Date()
      const currentMonthNumber = currentDate.getMonth() + 1
      const currentYear = currentDate.getFullYear()
      const isCurrentMonth = currentMonthNumber === monthNumber && currentYear === year
      
			// use current date as start date for current month, otherwise use the first date of the month 
      let dateToUse
      if (isCurrentMonth) {
        dateToUse = convertDateFormat(currentDate)
      } else {
        // Verwende die getFirstDateOfMonth Funktion für zukünftige Monate
        dateToUse = getFirstDateOfMonth(date)
      }
           // Use the new monthly endpoint instead of individual date calls
      this.$store
        .dispatch('getMonthlyAvailability', { date: dateToUse })
        .catch((error) => {
          console.error('Error fetching monthly availability:', error)
        })
    },
    closeCalendar() {
      if (this.showCalendar) document.querySelector('.termin__actions__date')?.click()
      this.$emit('closeCalendar')
    },
  },
}
</script>

<style lang="scss">
.date {
  &.el-dialog {
    width: fit-content !important;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12),
      0 6px 10px 0 rgba(0, 0, 0, 0.14);

    @media (min-width: $breakpoint-mobile) {
      position: absolute;
      top: 20px;
      left: -16px;
    }
  }
  .el-dialog__body {
    padding: 0 !important;
  }

  &__header {
    @include flex-center-align;
    justify-content: space-between;
    color: $color-text-secondary;
    padding: 16px;

    @media (min-width: $breakpoint-mobile) {
      padding: 12px 16px;
    }

    svg {
      cursor: pointer;
    }
  }

  &__calendar {
    padding: 16px;

    @media (min-width: $breakpoint-mobile) {
      padding: 16px 24px;
    }

    .el-popper__arrow,
    .el-date-editor {
      // date input
      display: none;
    }

    tr.el-date-table__row {
      &:hover {
        @media (min-width: $breakpoint-mobile) {
          background-color: $color-grey-lightest;
        }
      }

      .cell {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        height: 40px;
        width: 40px;
        border: 1px solid $color-grey-dark;
        background-color: white;
        color: $color-grey-darker;

        @media (min-width: $breakpoint-mobile) {
          height: 24px;
          width: 24px;
        }
      }

      &.current {
        .cell {
          border-color: $color-black;
          color: $color-text-primary;

          &.available {
            background-color: $color-black;
            border-color: $color-black;
            color: $color-text-white;
          }
        }
      }

      &:not(.current) {
        td {
          &.disabled {
            cursor: default;
          }

          &:not(.disabled) {
            .cell {
              &.selected {
                border-color: $color-black;
                color: $color-text-primary;
              }

              &.available {
                background-color: $color-text-secondary;
                border-color: $color-text-secondary;
                color: $color-text-white;

                &.selected {
                  background-color: $color-black;
                  border-color: $color-black;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
